import React from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import AgentHubSelect from './AgentHubSelect';
import { canSplitVertical, chartRangeOf } from './chartRange';
import {
  type AgentHubFilters,
  CADENCES,
  CHART_METRICS,
  CHART_RANGES,
  FILTER_DEFINITIONS,
  type FilterKey,
  SIGNUP_LEVELS,
  SPLITS,
  comparisonPresentationFor,
  defaultFilters,
  lastCompleteDay,
  validChartRangeCount,
} from './model';
import {
  CUSTOM_COMPARISONS,
  DATE_CADENCES,
  DATE_TYPES,
  SELECTION_COMPARISONS,
  chartCadenceOf,
  selectionComparisonOf,
} from './period';

type Props = {
  filters: AgentHubFilters;
  options: Record<string, Array<string>>;
  onChange: (change: Partial<AgentHubFilters>) => void;
};

/** Option lists stop at 500 values, so longer lists also accept a typed value. */
const OPTION_LIMIT = 500;

export default function AgentHubControls({ filters, options, onChange }: Props) {
  const select = (key: FilterKey, values: Array<string>) =>
    onChange({ selections: { ...filters.selections, [key]: values } });
  const chartRange = validChartRangeCount(filters.chartRangeCount) ? chartRangeOf(filters) : null;
  const cadence = filters.dateType === 'selection' ? chartCadenceOf(filters) : filters.cadence;
  const comparison = comparisonPresentationFor(filters.tab);
  const fields = FILTER_DEFINITIONS.filter(({ agents }) => filters.tab !== 'agents' || agents);
  return (
    <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
      <Stack gap={1.5}>
        <Typography variant="subtitle2">Dates</Typography>
        <AgentHubSelect
          label="Date type"
          value={filters.dateType}
          choices={DATE_TYPES}
          onChange={(dateType) => onChange({ dateType })}
        />
        {filters.dateType === 'selection' ? (
          <>
            <AgentHubSelect
              label="Cadence"
              value={filters.dateCadence}
              choices={DATE_CADENCES}
              onChange={(dateCadence) =>
                onChange({ dateCadence, selectionComparison: selectionComparisonOf({ ...filters, dateCadence }) })
              }
            />
            <TextField
              label="Date selection"
              type="date"
              size="small"
              value={filters.dateSelection}
              onChange={(event) => onChange({ dateSelection: event.target.value })}
              inputProps={{ max: lastCompleteDay() }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            {comparison.showControl && (
              <AgentHubSelect
                label="Comparison"
                value={selectionComparisonOf(filters)}
                choices={SELECTION_COMPARISONS[filters.dateCadence]}
                onChange={(selectionComparison) => onChange({ selectionComparison })}
              />
            )}
          </>
        ) : (
          <>
            <Stack direction="row" gap={1}>
              <TextField
                label="Start date"
                type="date"
                size="small"
                value={filters.from}
                onChange={(event) => onChange({ from: event.target.value })}
                inputProps={{ max: lastCompleteDay() }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End date"
                type="date"
                size="small"
                value={filters.to}
                onChange={(event) => onChange({ to: event.target.value })}
                inputProps={{ max: lastCompleteDay() }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            {comparison.showControl && (
              <AgentHubSelect
                label="Comparison"
                value={filters.customComparison}
                choices={CUSTOM_COMPARISONS}
                onChange={(customComparison) => onChange({ customComparison })}
              />
            )}
          </>
        )}
        {comparison.caption && (
          <Typography variant="caption" color="text.secondary">
            {comparison.caption}
          </Typography>
        )}
        {filters.tab !== 'agents' && (
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={filters.refunds}
            onChange={(_, value) => value && onChange({ refunds: value })}
            aria-label="Before or after refunds"
          >
            <ToggleButton value="BR">Before refunds (BR)</ToggleButton>
            <ToggleButton value="AR">After refunds (AR)</ToggleButton>
          </ToggleButtonGroup>
        )}
        {filters.dateType === 'custom' && (
          <AgentHubSelect
            label="Date cadence (chart)"
            value={filters.cadence}
            choices={CADENCES}
            onChange={(cadence) => onChange({ cadence })}
          />
        )}
        {filters.tab === 'orders' && (
          <>
            <AgentHubSelect
              label="Chart range"
              value={filters.chartRange}
              choices={CHART_RANGES}
              onChange={(chartRange) => onChange({ chartRange })}
            />
            {filters.chartRange !== 'default' && (
              <TextField
                label="Number of periods"
                type="number"
                size="small"
                value={filters.chartRangeCount}
                inputProps={{ min: 1, step: 1 }}
                error={!validChartRangeCount(filters.chartRangeCount)}
                helperText={
                  !validChartRangeCount(filters.chartRangeCount) ? 'Enter a positive whole number.' : undefined
                }
                onChange={(event) => onChange({ chartRangeCount: event.target.value })}
                fullWidth
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {filters.chartRange === 'default'
                ? 'Chart opens on the last 24 periods. Table and CSV show all loaded periods.'
                : chartRange
                  ? `${chartRange.from} to ${chartRange.to}.`
                  : ''}
              {chartRange?.clamped ? ' Clamped to the 60 months loaded by the trend chart.' : ''} Independent of KPI
              dates and comparison.
            </Typography>
            {(cadence === 'week' || cadence === 'month') && (
              <Typography variant="caption" color="text.secondary">
                Weekly and monthly bars show whole periods that overlap the range.
              </Typography>
            )}
            <AgentHubSelect
              label="Metric"
              value={filters.chartMetric}
              choices={CHART_METRICS}
              onChange={(chartMetric) => onChange({ chartMetric })}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filters.splitVertical && canSplitVertical(filters.chartMetric)}
                  disabled={!canSplitVertical(filters.chartMetric)}
                  onChange={(event) => onChange({ splitVertical: event.target.checked })}
                />
              }
              label="Split by product vertical"
            />
            {!canSplitVertical(filters.chartMetric) && (
              <Typography variant="caption" color="text.secondary">
                Product split is available for value measures.
              </Typography>
            )}
            <AgentHubSelect
              label="Data split"
              value={filters.split}
              choices={SPLITS}
              onChange={(split) => onChange({ split })}
            />
            {filters.split === 'agent_path' && (
              <AgentHubSelect
                label="Split levels"
                value={filters.depth}
                choices={[
                  { value: 1, label: 'Affiliation' },
                  { value: 2, label: 'Affiliation › Agency' },
                  { value: 3, label: 'Affiliation › Agency › Agent' },
                ]}
                onChange={(depth) => onChange({ depth: depth })}
              />
            )}
          </>
        )}
        {filters.tab === 'agents' && (
          <>
            <AgentHubSelect
              label="Sign-up metric"
              value={filters.signupLevel}
              choices={SIGNUP_LEVELS}
              onChange={(signupLevel) => onChange({ signupLevel })}
            />
            {(
              [
                ['showAffiliation', 'Show affiliation?'],
                ['showAgencies', 'Show agencies?'],
                ['showAgents', 'Show agents?'],
              ] as const
            ).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    size="small"
                    checked={filters[key]}
                    onChange={(event) => onChange({ [key]: event.target.checked })}
                  />
                }
                label={label}
              />
            ))}
          </>
        )}
        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          Filters
        </Typography>
        {filters.tab === 'orders' && (
          <>
            <Typography variant="subtitle2">Check-in date (order level)</Typography>
            <Stack direction="row" gap={1}>
              <TextField
                label="Check-in start"
                type="date"
                size="small"
                value={filters.checkInFrom}
                onChange={(event) => onChange({ checkInFrom: event.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Check-in end"
                type="date"
                size="small"
                value={filters.checkInTo}
                onChange={(event) => onChange({ checkInTo: event.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </>
        )}
        {fields.map(({ key, label }) => {
          const selected = filters.selections[key];
          const listed = options[key] ?? [];
          return (
            <Autocomplete
              key={key}
              multiple
              freeSolo={listed.length >= OPTION_LIMIT}
              disableCloseOnSelect
              size="small"
              limitTags={1}
              options={[...new Set([...listed, ...selected])]}
              value={selected}
              onChange={(_, values) => select(key, values.map(String))}
              renderTags={(values, getTagProps) =>
                values.map((option, index) => {
                  const { key: tagKey, ...tagProps } = getTagProps({ index });
                  return <Chip key={tagKey} {...tagProps} label={option} size="small" />;
                })
              }
              renderOption={(props, option, { selected: checked }) => (
                <Box component="li" {...props} key={option}>
                  <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
                  {option}
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label={label} placeholder={selected.length ? '' : 'All'} />
              )}
            />
          );
        })}
        <Button onClick={() => onChange(defaultFilters(filters.tab))}>Reset</Button>
      </Stack>
    </Paper>
  );
}
